
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
*/

import { CDN_URLS, OCI_FOLDERS } from './config';

export const NAV_LINKS = [
    { name: 'nav.home', page: 'home' },
    { name: 'nav.portfolio', page: 'portfolio' },
    { name: 'nav.services', page: 'services' },
    { name: 'nav.blog', page: 'blog' },
    { name: 'nav.trends', page: 'trends' },
    { name: 'nav.booking', page: 'booking' },
];

export const MEMBERSHIP_LIMITS = {
    free: 1,
    basic: 10,
    premium: 200,
    nextgen: 250,
    advanced: 5000,
    one_time: 150
};

export const STYLE_AGENT_LIMITS = {
    free: 1,
    basic: 3,
    premium: 15,
    nextgen: 20,
    advanced: 50,
    one_time: 10
};

export const membershipTiers = [
    {
        id: 'free',
        nameKey: 'profilePage_membership_free',
        featuresKey: [
            'membership_feature_free_1',
            'membership_feature_free_2',
        ],
        prices: [],
    },
    {
        id: 'basic',
        nameKey: 'profilePage_membership_basic',
        featuresKey: [
            'membership_feature_basic_1',
            'membership_feature_basic_2',
            'membership_feature_basic_3',
            'membership_feature_basic_4',
        ],
        prices: [
            { cycle: 'weekly', priceKey: 'price_basic_weekly', basePriceEUR: 9.99 },
            { cycle: 'monthly', priceKey: 'price_basic_monthly', discountKey: 'membership_discount_monthly', basePriceEUR: 29.99 },
            { cycle: 'quarterly', priceKey: 'price_basic_quarterly', discountKey: 'membership_discount_quarterly', basePriceEUR: 79.99 },
        ],
    },
    {
        id: 'premium',
        nameKey: 'profilePage_membership_premium',
        isFeatured: true,
        featuresKey: [
            'membership_feature_premium_1',
            'membership_feature_premium_2',
            'membership_feature_premium_3',
            'membership_feature_premium_4',
            'membership_feature_premium_5',
            'membership_feature_premium_style_tips',
        ],
        prices: [
            { cycle: 'weekly', priceKey: 'price_premium_weekly', basePriceEUR: 19.99 },
            { cycle: 'monthly', priceKey: 'price_premium_monthly', discountKey: 'membership_discount_monthly', basePriceEUR: 59.99 },
            { cycle: 'quarterly', priceKey: 'price_premium_quarterly', discountKey: 'membership_discount_quarterly', basePriceEUR: 159.99 },
        ],
    },
    {
        id: 'advanced',
        nameKey: 'profilePage_membership_advanced',
        featuresKey: [
            'membership_feature_advanced_1',
            'membership_feature_advanced_2',
            'membership_feature_advanced_3',
            'membership_feature_advanced_4',
            'membership_feature_advanced_5',
            'membership_feature_advanced_social_media',
            'membership_feature_advanced_plan_integration',
            'membership_feature_advanced_automated_shopping',
        ],
        prices: [
            { cycle: 'weekly', priceKey: 'price_advanced_weekly', basePriceEUR: 39.99 },
            { cycle: 'monthly', priceKey: 'price_advanced_monthly', discountKey: 'membership_discount_monthly', basePriceEUR: 119.99 },
            { cycle: 'quarterly', priceKey: 'price_advanced_quarterly', discountKey: 'membership_discount_quarterly', basePriceEUR: 319.99 },
        ],
    },
    {
        id: 'nextgen',
        nameKey: 'profilePage_membership_nextgen',
        featuresKey: [
            'membership_feature_nextgen_1',
            'membership_feature_nextgen_2',
            'membership_feature_nextgen_3',
            'membership_feature_nextgen_4',
            'membership_feature_premium_1',
        ],
        prices: [
            { cycle: 'weekly', priceKey: 'price_nextgen_weekly', basePriceEUR: 4.99 },
            { cycle: 'monthly', priceKey: 'price_nextgen_monthly', discountKey: 'membership_discount_monthly', basePriceEUR: 14.99 },
        ],
    }
];

export const SYSTEM_AGENTS = [
    { id: 'fashionExpert', nameKey: 'stylingAdvisorPage_agent_fashionExpert_name', descriptionKey: 'stylingAdvisorPage_agent_fashionExpert_description', requiredMembership: 'basic' },
    { id: 'minimalistStylist', nameKey: 'stylingAdvisorPage_agent_minimalistStylist_name', descriptionKey: 'stylingAdvisorPage_agent_minimalistStylist_description', requiredMembership: 'basic' },
    { id: 'styleQuestionnaire', nameKey: 'stylingAdvisorPage_agent_styleQuestionnaire_name', descriptionKey: 'stylingAdvisorPage_agent_styleQuestionnaire_description', requiredMembership: 'basic' },
    { id: 'business', nameKey: 'stylingAdvisorPage_agent_business_name', descriptionKey: 'stylingAdvisorPage_agent_business_description', requiredMembership: 'premium' },
    { id: 'vogueEditor', nameKey: 'stylingAdvisorPage_agent_vogueEditor_name', descriptionKey: 'stylingAdvisorPage_agent_vogueEditor_description', requiredMembership: 'premium' },
    { id: 'glamour', nameKey: 'stylingAdvisorPage_agent_glamour_name', descriptionKey: 'stylingAdvisorPage_agent_glamour_description', requiredMembership: 'advanced' },
];

export const ON_DEMAND_FEATURES = [
    {
        id: 'wardrobe_3d',
        nameKey: 'on_demand_feature_wardrobe_3d_title',
        descriptionKey: 'on_demand_feature_wardrobe_3d_desc',
        priceEUR: 2.99,
        priceKey: 'on_demand_feature_wardrobe_3d_price',
        durationHours: 24,
    },
    {
        id: 'runway_video',
        nameKey: 'on_demand_feature_runway_video_title',
        descriptionKey: 'on_demand_feature_runway_video_desc',
        priceEUR: 4.99,
        priceKey: 'on_demand_feature_runway_video_price',
        amount: 1,
    },
    {
        id: 'style_report',
        nameKey: 'on_demand_feature_style_report_title',
        descriptionKey: 'on_demand_feature_style_report_desc',
        priceEUR: 9.99,
        priceKey: 'on_demand_feature_style_report_price',
        amount: 1,
    },
    {
        id: 'color_analysis',
        nameKey: 'on_demand_feature_color_analysis_title',
        descriptionKey: 'on_demand_feature_color_analysis_desc',
        priceEUR: 3.99,
        priceKey: 'on_demand_feature_color_analysis_price',
        amount: 1,
    },
    {
        id: 'color_report',
        nameKey: 'on_demand_feature_color_report_title',
        descriptionKey: 'on_demand_feature_color_report_desc',
        priceEUR: 14.99,
        priceKey: 'on_demand_feature_color_report_price',
        durationHours: 720, // 30 days
    },
    {
        id: 'magazine_lookbook',
        nameKey: 'on_demand_feature_magazine_lookbook_title',
        descriptionKey: 'on_demand_feature_magazine_lookbook_desc',
        priceEUR: 5.99,
        priceKey: 'on_demand_feature_magazine_lookbook_price',
        amount: 1,
    },
    {
        id: 'capsule_generator',
        nameKey: 'on_demand_feature_capsule_generator_title',
        descriptionKey: 'on_demand_feature_capsule_generator_desc',
        priceEUR: 4.99,
        priceKey: 'on_demand_feature_capsule_generator_price',
        amount: 1,
    },
];

export const HERO_IMAGES: string[] = [
    'photos/1884581/pexels-photo-1884581.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2',
    'photos/179909/pexels-photo-179909.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2',
    'photos/322207/pexels-photo-322207.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2',
    'photos/1036627/pexels-photo-1036627.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2'
];

export const GOOGLE_PHOTOS_EXAMPLES = [
    { id: 'gp-1', baseUrl: 'AF1QipM7l_example_id_1', filename: 'summer_style_1.jpg', mimeType: 'image/jpeg' },
    { id: 'gp-2', baseUrl: 'AF1QipM7l_example_id_2', filename: 'winter_look_2.jpg', mimeType: 'image/jpeg' }
];

export const FASHION_KEYWORDS: string[] = [
    'Fashion Week', 'Runway', 'Designer Collections', 'Haute Couture'
];

export const BACKGROUND_CATEGORIES = [
    {
        nameKey: "background_category_studio_abstract",
        backgrounds: [
            "Professional Studio Grey", "Clean, neutral studio backdrop (light gray, #f0f0f0)", "Minimalist Concrete Wall",
            "Gradient Sky (Soft Pastel)", "Soft Bokeh Lights", "Dark Moody Textured Background", "Abstract Geometric Shapes",
            "Neon Light Tunnel", "Clean White Cyc Wall", "Soft Peach and Cream Gradient"
        ]
    },
    {
        nameKey: "background_category_outdoor_cityscapes",
        backgrounds: [
            "Parisian Cafe Outdoors", "Walking along the Seine in Paris with historic bridges in the background.", "View of the Eiffel Tower from a Parisian balcony.",
            "Riding a Gondola in the canals of Venice, Italy.", "Standing on the Rialto Bridge, Venice at sunset.", "Gardens by the Bay, Singapore, with Supertrees in the background.",
            "Marina Bay Sands SkyPark observation deck, Singapore.", "Brandenburg Gate, Berlin, on a sunny afternoon.", "Trendy street art district in Kreuzberg, Berlin.",
            "Old Town Zurich by the Limmat river with Grossmünster in view.", "Luxury shopping street Bahnhofstrasse, Zurich.",
            "Walking through the historic First District of Vienna, surrounded by imperial palaces.",
            "An elegant evening stroll near the Vienna State Opera, illuminated at night.",
            "Wandering through a sunlit piazza in Rome with ancient fountains and baroque architecture.",
            "A charming cobblestone street in Trastevere, Rome, with warm ochre walls and ivy.",
            "Walking through the medieval Old Town of Bern, Switzerland, with the Zytglogge clock tower.",
            "Strolling under the historic sandstone arcades (Lauben) of Bern.", "A foggy morning walk across the Charles Bridge in Prague.",
            "Elegant street in Kensington, London with white Victorian townhouses.", "Enjoying a coffee at an outdoor city cafe, looking relaxed and candid.",
            "Neon City Street", "Candid city walk, crossing a stylish urban street, captured mid-stride with natural movement.",
            "Busy Downtown Crosswalk", "Walking through a Busy Downtown Crosswalk, yellow cabs in background, urban energy.", "Subway Station Platform"
        ]
    },
    {
        nameKey: "background_category_luxury_fashion_studio",
        backgrounds: [
            "High-end fashion studio with softbox lighting and white cyclorama", "Editorial fashion set with dramatic spotlight and shadow play",
            "Minimalist luxury studio with marble floor and soft diffuse light", "Avant-garde fashion set with suspended geometric shapes",
            "Vintage vogue-style studio with painted canvas backdrop", "Modern industrial studio with exposed brick and large windows",
            "Luxury showroom with velvet curtains and gold accents", "Futuristic fashion lab with clean white surfaces and cool lighting",
            "Warm, golden-hour studio setup with natural light simulation", "Dark, moody fashion studio with rim lighting"
        ]
    },
    {
        nameKey: "background_category_luxury_indoor",
        backgrounds: [
            "Luxury Hotel Lobby", "Grand Ballroom of a Victorian Palace.", "Medieval Stone Castle courtyard with cobblestones and banners.",
            "Cozy Living Room", "Red Carpet Premiere with Flashes", "Hollywood Walk of Fame", "Luxury Beverly Hills Mansion Pool",
            "Vintage Hollywood Film Set", "Grand Theater Staircase", "Award Show Stage with Spotlight", "Modern Corporate Office",
            "Luxury Hotel Coffee Bar", "Rooftop Lounge at Sunset", "Minimalist Art Gallery", "Modern Art Museum Hallway",
            "High-End Fashion Boutique", "Executive Boardroom with City View", "Minimalist Architectural Staircase",
            "Classic Library with Wood Paneling", "Urban Loft with Brick Walls", "Marble Foyer",
            "Professional fashion pose in a Christmas decorated luxury hotel lounge, Vienna.",
            "Relaxed elegant pose sitting in a velvet chair in a festive 5-star hotel lobby, Paris.",
            "High-fashion stance in a decorated arcade in Milan (Galleria Vittorio Emanuele II) during holidays.",
            "High-stakes Casino Floor with slot machines and card tables", "Luxury Superyacht Deck overlooking the Mediterranean Sea",
            "Grand Opera Hall with red velvet seats and gold balconies", "Classic Castle Hall with high stone arches and tapestries",
            "Glamorous Fashion Week Runway with audience and lights", "Penthouse Living Room with panoramic city skyline view",
            "Private Jet Interior with leather seats and champagne", "Exclusive Wine Cellar with vaulted brick ceiling",
            "Luxury Spa with indoor pool and stone walls", "Royal Palace Hallway with gilded mirrors and chandeliers",
            "Fashion photoshoot inside the historic Casino Bern with red velvet", "Elegant fashion pose in the grand lobby of the Bellevue Palace Hotel in Bern"
        ]
    },
    {
        nameKey: "background_category_luxury_evening_hotel",
        backgrounds: [
            "Elegant hotel lounge with velvet armchairs and dim lighting", "Chic hotel bar with backlit shelves and marble counter",
            "Luxury hotel rooftop terrace at twilight with city lights", "Cozy fireplace corner in a high-end hotel lobby",
            "Sophisticated piano bar with warm ambiance", "Private dining room with candlelight and crystal glasses",
            "Grand hotel corridor with plush carpet and sconces", "Modern luxury hotel suite living area with night view",
            "Stylish speakeasy bar with leather seating and moody atmosphere", "Opulent hotel entrance with revolving door and bellhop cart"
        ]
    },
    {
        nameKey: "background_category_nature_seasonal",
        backgrounds: [
            "Sunny Tropical Beach", "Snowy Winter Forest", "Botanical Garden Greenhouse", "Fairytale Neuschwanstein Castle on a hill.",
            "Sunny Park Bench", "Leaning against a railing with a city skyline in the background at golden hour, looking thoughtful.",
            "A rustic ski chalet interior, dominated by a large stone fireplace with a roaring fire.",
            "A scene of friends gathered in a cozy ski chalet after a day of skiing.", "An elegant ski chalet living room with polished wood floors and large windows.",
            "A classic wooden ski chalet covered in a thick blanket of fresh snow.", "An atmospheric view of a ski lodge at night during a gentle snowfall.",
            "Relaxed seated pose in an elegant ski chalet living room with large windows.",
            "Relaxed apreski pose, sitting inside a cozy wood chalet by the window.", "Dynamic and fun pose on a sunny ski slope during a party.",
            "A candid walk towards a ski lodge at night during a gentle snowfall.", "Festive pose, standing by a beautifully decorated Christmas tree.",
            "Sitting in a photorealistic, panoramic view of snow-covered Swiss Alps at sunset.",
            "Artistic pose, standing in winter landscape with a breathtaking wide shot of a mountain village.",
            "Energetic pose, captured in an aerial, panoramic shot over a vast, snow-laden pine forest.", "Joyful pose, in photorealistic stunning winter panoramic view.",
            "A candid walk in pine forest to an alpine resort winter retreat.",
            "Standing on the steps of Vienna City Hall (Rathaus) illuminated for the Christmas market.",
            "Walking candidly through the Vienna Christmas Dream market, surrounded by lights.",
            "Posing with a hot cocoa mug at the Bern Minster Christmas market, snowy evening.",
            "Leaning against a stall at a traditional Christmas market in Strasbourg, warm glow.",
            "Full body shot in front of the Galeries Lafayette Christmas window displays, Paris.",
            "Walking along the Graben in Vienna under the famous giant chandelier Christmas lights.",
            "Sitting on a bench in a snowy park near the Eiffel Tower, wrapped in a winter coat.",
            "Advanced dynamic pose in front of the Zurich Opera House with holiday projections.",
            "Candid laughing pose amidst the bustle of a Berlin Christmas market at Gendarmenmarkt.",
            "Elegant green pine branches with subtle gold ornaments, soft bokeh background.",
            "Simple dark green velvet backdrop with a single minimalist wreath.", "Softly lit evergreen foliage with delicate white fairy lights.",
            "Minimalist Christmas setup with emerald green ribbons and pine cones."
        ]
    }
];


export const PORTFOLIO_ITEMS = [
  {
    id: 'p-1',
    titleKey: 'portfolio.item1.title',
    descriptionKey: 'portfolio.item1.desc',
    imageUrl: `${CDN_URLS.OCI_STORE}${OCI_FOLDERS.CONTENT}city/schwarz_el_bern.png`,
    categoryKey: 'portfolio_category_editorial',
    pricingTiers: []
  },
  {
    id: 'p-2',
    titleKey: 'portfolio.item2.title',
    descriptionKey: 'portfolio.item2.desc',
    imageUrl: `${CDN_URLS.OCI_STORE}${OCI_FOLDERS.CONTENT}Casual/city_style.png`,
    categoryKey: 'portfolio_category_street',
    pricingTiers: []
  },
  {
    id: 'p-3',
    titleKey: 'portfolio.item3.title',
    descriptionKey: 'portfolio.item3.desc',
    imageUrl: `${CDN_URLS.OCI_STORE}${OCI_FOLDERS.CONTENT}Elegant/elegant_blau.png`,
    categoryKey: 'portfolio_category_commercial',
    pricingTiers: []
  },
  {
    id: 'p-4',
    titleKey: 'portfolio.item4.title',
    descriptionKey: 'portfolio.item4.desc',
    imageUrl: `${CDN_URLS.OCI_STORE}${OCI_FOLDERS.CONTENT}Elegant/city_herbst_blau_elegance.png`,
    categoryKey: 'portfolio_category_editorial',
    pricingTiers: []
  },
  {
    id: 'p-5',
    titleKey: 'portfolio.item5.title',
    descriptionKey: 'portfolio.item5.desc',
    imageUrl: `${CDN_URLS.OCI_STORE}${OCI_FOLDERS.CONTENT}Elegant/elegant_mantel2.png`,
    categoryKey: 'portfolio_category_portraits',
    pricingTiers: []
  },
  {
    id: 'p-gen-alpha',
    titleKey: 'portfolio.alpha.title',
    descriptionKey: 'portfolio.alpha.desc',
    imageUrl: `${CDN_URLS.OCI_STORE}${OCI_FOLDERS.CONTENT}Naature/casual_nature.png`,
    categoryKey: 'generation_alpha',
    pricingTiers: []
  },
  {
    id: 'p-gen-z',
    titleKey: 'portfolio.z.title',
    descriptionKey: 'portfolio.z.desc',
    imageUrl: `${CDN_URLS.OCI_STORE}${OCI_FOLDERS.CONTENT}portrait/20230917_145536.jpg`,
    categoryKey: 'generation_z',
    pricingTiers: []
  },
  {
    id: 'p-gen-millennial',
    titleKey: 'portfolio.millennial.title',
    descriptionKey: 'portfolio.millennial.desc',
    imageUrl: `${CDN_URLS.OCI_STORE}${OCI_FOLDERS.CONTENT}Sport/sport_2.png`,
    categoryKey: 'generation_millennial',
    pricingTiers: []
  },
  {
    id: 'p-zara-2026',
    titleKey: 'campaign.zara.title',
    descriptionKey: 'campaign.zara.desc',
    imageUrl: `${CDN_URLS.OCI_STORE}${OCI_FOLDERS.CONTENT}city/elegent_cafe_grau_rock.png`,
    categoryKey: 'portfolio_category_commercial',
    pricingTiers: []
  },
  {
    id: 'p-hm-2026',
    titleKey: 'campaign.hm.title',
    descriptionKey: 'campaign.hm.desc',
    imageUrl: `${CDN_URLS.OCI_STORE}${OCI_FOLDERS.CONTENT}Elegant/elegant_schwarz_stairs.png`,
    categoryKey: 'portfolio_category_editorial',
    pricingTiers: []
  },
  {
    id: 'p-mango-2026',
    titleKey: 'campaign.mango.title',
    descriptionKey: 'campaign.mango.desc',
    imageUrl: `${CDN_URLS.OCI_STORE}${OCI_FOLDERS.CONTENT}Elegant/elegant_gray_art_studio.png`,
    categoryKey: 'portfolio_category_editorial',
    pricingTiers: []
  },
  {
    id: 'p-zalando-2026',
    titleKey: 'campaign.zalando.title',
    descriptionKey: 'campaign.zalando.desc',
    imageUrl: `${CDN_URLS.OCI_STORE}${OCI_FOLDERS.CONTENT}Elegant/elegant_mantel2.png`,
    categoryKey: 'portfolio_category_commercial',
    pricingTiers: []
  },
  {
    id: 'p-pkz-2026',
    titleKey: 'campaign.pkz.title',
    descriptionKey: 'campaign.pkz.desc',
    imageUrl: `${CDN_URLS.OCI_STORE}${OCI_FOLDERS.CONTENT}Elegant/elegant_444.png`,
    categoryKey: 'portfolio_category_editorial',
    pricingTiers: []
  }
];

export const GENERATIONAL_TRENDS = [
  {
    id: 1,
    titleKey: "trend_alpha_digital_twin_title",
    brand: "RTFKT x Nike",
    imageUrl: `${CDN_URLS.OCI_STORE}${OCI_FOLDERS.CONTENT}Elegant/AutoSalon.png`,
    price: "€210",
    tagKey: "trend_tag_ai_native",
    generation: "alpha"
  },
  {
    id: 2,
    titleKey: "trend_alpha_gaming_couture_title",
    brand: "Balenciaga x Roblox",
    imageUrl: `${CDN_URLS.OCI_STORE}${OCI_FOLDERS.CONTENT}Naature/white_meer_strand.JPG`,
    price: "€1450",
    tagKey: "trend_tag_metaverse",
    generation: "alpha"
  },
  {
    id: 3,
    titleKey: "trend_z_y2k_redux_title",
    brand: "Heaven by Marc Jacobs",
    imageUrl: `${CDN_URLS.OCI_STORE}${OCI_FOLDERS.CONTENT}Casual/herbs_bordeaux_walking.png`,
    price: "€185",
    tagKey: "trend_tag_nostalgia",
    generation: "z"
  },
  {
    id: 4,
    titleKey: "trend_z_thrifting_elite_title",
    brand: "Depop Curated",
    imageUrl: `${CDN_URLS.OCI_STORE}${OCI_FOLDERS.CONTENT}Casual/casual_city_1.png`,
    price: "€45",
    tagKey: "trend_tag_eco_conscious",
    generation: "z"
  },
  {
    id: 5,
    titleKey: "trend_millennial_80s_glam_title",
    brand: "Saint Laurent",
    imageUrl: `${CDN_URLS.OCI_STORE}${OCI_FOLDERS.CONTENT}city/elegant_white_black_rock.png`,
    price: "€2800",
    tagKey: "trend_tag_sophisticated",
    generation: "millennial"
  },
  {
    id: 6,
    titleKey: "trend_millennial_quiet_luxury_title",
    brand: "The Row",
    imageUrl: `${CDN_URLS.OCI_STORE}${OCI_FOLDERS.CONTENT}city/elegant_walk_bourboun.png`,
    price: "€3200",
    tagKey: "trend_tag_timeless",
    generation: "millennial"
  },
  {
    id: 7,
    titleKey: "trend_classic_heritage_title",
    brand: "Barbour",
    imageUrl: `${CDN_URLS.OCI_STORE}${OCI_FOLDERS.CONTENT}Elegant/download (30).jpg`,
    price: "€450",
    tagKey: "trend_tag_heritage",
    generation: "classic"
  },
  {
    id: 8,
    titleKey: "campaign.zara.title",
    brand: "ZARA SS26",
    imageUrl: `${CDN_URLS.OCI_STORE}${OCI_FOLDERS.CONTENT}city/elenag_cravatte_city_grau.png`,
    price: "€89",
    tagKey: "trend_tag_sculpted",
    generation: "z"
  },
  {
    id: 9,
    titleKey: "campaign.hm.title",
    brand: "H&M x Jacques Wei",
    imageUrl: `${CDN_URLS.OCI_STORE}${OCI_FOLDERS.CONTENT}city/elegent_cafe_grau_rock.png`,
    price: "€129",
    tagKey: "trend_tag_heritage_fusion",
    generation: "alpha"
  },
  {
    id: 10,
    titleKey: "campaign.mango.title",
    brand: "Mango Teen",
    imageUrl: `${CDN_URLS.OCI_STORE}${OCI_FOLDERS.CONTENT}Elegant/elegant_mantel2.png`,
    price: "€55",
    tagKey: "trend_tag_ai_vision",
    generation: "alpha"
  },
  {
    id: 11,
    titleKey: "campaign.zalando.title",
    brand: "Zalando Visionary",
    imageUrl: `${CDN_URLS.OCI_STORE}${OCI_FOLDERS.CONTENT}Elegant/elegant_braun_bern_arkaden.png`,
    price: "€320",
    tagKey: "trend_tag_emerging",
    generation: "millennial"
  },
  {
    id: 12,
    titleKey: "campaign.pkz.title",
    brand: "PKZ Zurich",
    imageUrl: `${CDN_URLS.OCI_STORE}${OCI_FOLDERS.CONTENT}Elegant/download - 2026-02-21T151907.305.png`,
    price: "€450",
    tagKey: "trend_tag_alpine_chic",
    generation: "classic"
  },
  {
    id: 13,
    titleKey: "trend_alpha_cyber_soft_title",
    brand: "Acne Studios 2026",
    imageUrl: `${CDN_URLS.OCI_STORE}${OCI_FOLDERS.CONTENT}Sport/sport_2.png`,
    price: "€340",
    tagKey: "trend_tag_cyber_soft",
    generation: "alpha"
  },
  {
    id: 14,
    titleKey: "trend_z_poet_core_title",
    brand: "Gucci Revival",
    imageUrl: `${CDN_URLS.OCI_STORE}${OCI_FOLDERS.CONTENT}Elegant/Weiss_El.png`,
    price: "€890",
    tagKey: "trend_tag_poet_core",
    generation: "z"
  },
  {
    id: 15,
    titleKey: "trend_millennial_glamoratti_title",
    brand: "Versace Gold",
    imageUrl: `${CDN_URLS.OCI_STORE}${OCI_FOLDERS.CONTENT}Elegant/download - 2026-02-21T152557.479.png`,
    price: "€2100",
    tagKey: "trend_tag_glamoratti",
    generation: "millennial"
  },
  {
    id: 16,
    titleKey: "trend_classic_guardian_title",
    brand: "Max Mara Shield",
    imageUrl: `${CDN_URLS.OCI_STORE}${OCI_FOLDERS.CONTENT}Elegant/el_1_schwarz.png`,
    price: "€1250",
    tagKey: "trend_tag_guardian",
    generation: "classic"
  }
];

export const SOCIAL_LINKS = [
  {
    name: 'Facebook',
    url: 'https://facebook.com',
    iconPath: 'M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z'
  },
  {
    name: 'Twitter',
    url: 'https://twitter.com',
    iconPath: 'M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z'
  },
  {
    name: 'Instagram',
    url: 'https://instagram.com',
    iconPath: 'M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z'
  },
  {
    name: 'LinkedIn',
    url: 'https://linkedin.com',
    iconPath: 'M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z'
  }
];

export const FOOTER_TEXT = {
  COPYRIGHT: `© ${new Date().getFullYear()} The Signature Styling Advisor. All rights reserved.`,
  PRIVACY_POLICY: 'Privacy Policy',
  TERMS_OF_SERVICE: 'Terms of Service',
};

export const POSE_CATEGORIES = [
  {
    nameKey: "pose_category_standard",
    poses: [
      { instruction: "A standard, relaxed standing model pose, 3/4 view towards camera.", labelKey: "pose_label_3_4_view", previewUrl: `${CDN_URLS.UNSPLASH}photo-1509631179647-0177331693ae?w=400` },
      { instruction: "A simple front-facing standing pose.", labelKey: "pose_label_front_view", previewUrl: `${CDN_URLS.UNSPLASH}photo-1488161628813-04466f872be2?w=400` },
      { instruction: "A side profile standing pose.", labelKey: "pose_label_side", previewUrl: `${CDN_URLS.UNSPLASH}photo-1496747611176-843222e1e57c?w=400` },
      { instruction: "A back view standing pose.", labelKey: "pose_label_back_view", previewUrl: `${CDN_URLS.UNSPLASH}photo-1534528741775-53994a69daeb?w=400` },
      { instruction: "Model walking towards the camera.", labelKey: "pose_label_walking", previewUrl: `${CDN_URLS.UNSPLASH}photo-1518310383802-640c2de311b2?w=400` },
      { instruction: "Model walking away from the camera.", labelKey: "pose_label_walking_away", previewUrl: `${CDN_URLS.UNSPLASH}photo-1507525428034-b723cf961d3e?w=400` },
      { instruction: "Full frontal view, hands on hips", labelKey: "pose_label_frontal_hips", previewUrl: `${CDN_URLS.UNSPLASH}photo-1515886657613-9f3515b0c78f?w=400` },
      { instruction: "Standing tall, adjusting collar", labelKey: "pose_label_collar_adjust", previewUrl: `${CDN_URLS.UNSPLASH}photo-1515886657613-9f3515b0c78f?w=400` },
      { instruction: "Hand on hip, confident stance", labelKey: "pose_label_confident_hip", previewUrl: `${CDN_URLS.UNSPLASH}photo-1515886657613-9f3515b0c78f?w=400` },
    ]
  },
  {
    nameKey: "pose_category_athletic",
    requiredMembership: 'premium',
    poses: [
      { instruction: "Dynamic and sporty pose, ready for action, perfect for athleisure and activewear.", labelKey: "pose_label_athleisure", previewUrl: `${CDN_URLS.UNSPLASH}photo-1518310383802-640c2de311b2?w=400` },
      { instruction: "An athletic jump pose, dynamic action.", labelKey: "pose_label_athletic_jump", previewUrl: `${CDN_URLS.UNSPLASH}photo-1534438327276-14e5300c3a48?w=400` },
      { instruction: "A yoga-inspired side stretch pose.", labelKey: "pose_label_yoga_stretch", previewUrl: `${CDN_URLS.UNSPLASH}photo-1544367567-0f2fcb009e0b?w=400` },
      { instruction: "A sprinter in starting blocks position.", labelKey: "pose_label_sprinting_start", previewUrl: `${CDN_URLS.UNSPLASH}photo-1534438327276-14e5300c3a48?w=400` },
      { instruction: "Mid-air, performing a high kick, dynamic.", labelKey: "pose_label_high_kick", previewUrl: `${CDN_URLS.UNSPLASH}photo-1534438327276-14e5300c3a48?w=400` },
      { instruction: "Holding a basketball, ready to shoot.", labelKey: "pose_label_basketball_shot", previewUrl: `${CDN_URLS.UNSPLASH}photo-1519861531473-920026393112?w=400` },
      { instruction: "Yoga warrior pose", labelKey: "pose_label_yoga_warrior", previewUrl: `${CDN_URLS.UNSPLASH}photo-1515886657613-9f3515b0c78f?w=400` },
      { instruction: "Arms raised in victory", labelKey: "pose_label_victory", previewUrl: `${CDN_URLS.UNSPLASH}photo-1515886657613-9f3515b0c78f?w=400` },
      { instruction: "Dynamic lunge, fitness pose", labelKey: "pose_label_dynamic_lunge", previewUrl: `${CDN_URLS.UNSPLASH}photo-1515886657613-9f3515b0c78f?w=400` },
    ]
  },
  {
    nameKey: "pose_category_professional",
    requiredMembership: 'premium',
    poses: [
      { instruction: "Relaxed seated pose, legs crossed, hands resting.", labelKey: "pose_label_relaxed_seated", previewUrl: `${CDN_URLS.UNSPLASH}photo-1517841905240-472988babdf9?w=400` },
      { instruction: "Professional seated pose at a desk, looking forward.", labelKey: "pose_label_desk_seated", previewUrl: `${CDN_URLS.UNSPLASH}photo-1554415707-6e8cfc93fe23?w=400` },
      { instruction: "Walking with a professional briefcase or bag.", labelKey: "pose_label_professional_walk", previewUrl: `${CDN_URLS.UNSPLASH}photo-1497215728101-856f4ea42174?w=400` },
      { instruction: "Standing with a subtle lean against a wall, architectural background.", labelKey: "pose_label_architectural_lean", previewUrl: `${CDN_URLS.UNSPLASH}photo-1517841905240-472988babdf9?w=400` },
      { instruction: "Seated in a minimalist chair, high-fashion attitude.", labelKey: "pose_label_fashion_seated", previewUrl: `${CDN_URLS.UNSPLASH}photo-1517841905240-472988babdf9?w=400` },
      { instruction: "Relaxed seated pose, arms on knees.", labelKey: "pose_label_arms_on_knees", previewUrl: `${CDN_URLS.UNSPLASH}photo-1517841905240-472988babdf9?w=400` },
      { instruction: "Standard standing pose, adjusted for a seasonal city background.", labelKey: "pose_label_seasonal_city", previewUrl: `${CDN_URLS.UNSPLASH}photo-1517841905240-472988babdf9?w=400` },
      { instruction: "Standing confidently at a podium, giving a presentation.", labelKey: "pose_label_podium", previewUrl: `${CDN_URLS.UNSPLASH}photo-1515168833906-d2a3b82b302a?w=400` },
      { instruction: "Collaborating with a colleague, looking at a tablet.", labelKey: "pose_label_collaboration", previewUrl: `${CDN_URLS.UNSPLASH}photo-1556761175-b413da4baf72?w=400` },
      { instruction: "Arms crossed confidently, standing tall against a blurred office background.", labelKey: "pose_label_arms_crossed", previewUrl: `${CDN_URLS.UNSPLASH}photo-1573497019940-1c28c88b4f3e?w=400` },
      { instruction: "Holding a notebook and pen, looking attentive and ready to take notes.", labelKey: "pose_label_taking_notes", previewUrl: `${CDN_URLS.UNSPLASH}photo-1580894732444-8ecded7900cd?w=400` },
      { instruction: "A friendly, welcoming handshake pose, extending a hand towards the camera.", labelKey: "pose_label_handshake", previewUrl: `${CDN_URLS.UNSPLASH}photo-1560250097-0b93528c311a?w=400` },
      { instruction: "Sitting comfortably with a laptop, looking efficient and focused.", labelKey: "pose_label_laptop_work", previewUrl: `${CDN_URLS.UNSPLASH}photo-1573164713714-d95e436ab8d6?w=400` },
      { instruction: "Standing profile, checking a wristwatch, looking punctual and sharp.", labelKey: "pose_label_checking_watch", previewUrl: `${CDN_URLS.UNSPLASH}photo-1535713875002-d1d0cf377fde?w=400` },
      { instruction: "Holding a coffee cup, taking a break, looking relaxed but polished.", labelKey: "pose_label_coffee_break", previewUrl: `${CDN_URLS.UNSPLASH}photo-1589386417686-0d34b5903d23?w=400` },
      { instruction: "Working in a Modern Corporate Office, standing by a glass desk with city skyline view.", labelKey: "pose_label_modern_office", previewUrl: `${CDN_URLS.UNSPLASH}photo-1497215728101-856f4ea42174?w=400` },
      { instruction: "Leading a meeting in an Executive Boardroom with City View, standing at head of table.", labelKey: "pose_label_boardroom", previewUrl: `${CDN_URLS.UNSPLASH}photo-1507679799987-c73779587ccf?w=400` },
      { instruction: "Reading in a Classic Library with Wood Paneling, leaning against a bookshelf.", labelKey: "pose_label_classic_library", previewUrl: `${CDN_URLS.UNSPLASH}photo-1507842217343-583bb7270b66?w=400` },
      { instruction: "Walking, jacket slung over shoulder", labelKey: "pose_label_jacket_shoulder", previewUrl: `${CDN_URLS.UNSPLASH}photo-1515886657613-9f3515b0c78f?w=400` },
    ]
  },
  {
    nameKey: "pose_category_seasonal_professional",
    requiredMembership: 'premium',
    poses: [
      { instruction: "A cheerful spring pose, standing in a field of wildflowers, holding a single flower.", labelKey: "pose_label_spring_awakening", previewUrl: `${CDN_URLS.UNSPLASH}photo-1515886657613-9f3515b0c78f?w=400` },
      { instruction: "A relaxed summer pose, leaning against a palm tree on a sunny beach.", labelKey: "pose_label_summer_breeze", previewUrl: `${CDN_URLS.UNSPLASH}photo-1507525428034-b723cf961d3e?w=400` },
      { instruction: "Walking in a gentle snowfall in a city park at dusk with icelake behind, holding a warm cup.", labelKey: "pose_label_winter_city_park", previewUrl: `${CDN_URLS.UNSPLASH}photo-1515886657613-9f3515b0c78f?w=400` },
      { instruction: "Sitting on a ski lift with an panoramic view of snowy mountain photorealistic in background.", labelKey: "pose_label_ski_lift", previewUrl: `${CDN_URLS.UNSPLASH}photo-1418985991508-e47386d96a71?w=400` },
      { instruction: "Standing on a mountain top next to skis, looking at a panoramic view of snowy peaks.", labelKey: "pose_label_winter_mountain_view", previewUrl: `${CDN_URLS.UNSPLASH}photo-1454496522488-7a8e488e8606?w=400` },
      { instruction: "Joyful pose, building a snowman in a snowy backyard with mountain landscape, smiling at the camera.", labelKey: "pose_label_snowman", previewUrl: `${CDN_URLS.UNSPLASH}photo-1515886657613-9f3515b0c78f?w=400` },
      { instruction: "A cozy autumn pose, walking through a park with colorful falling leaves, holding a warm drink.", labelKey: "pose_label_autumn_stroll", previewUrl: `${CDN_URLS.UNSPLASH}photo-1508435234994-67cfd7690508?w=400` },
      { instruction: "Celebratory pose, holding a glass of champagne with confetti falling against a dark, sparkling background, looking excited for the New Year.", labelKey: "pose_label_new_year", previewUrl: `${CDN_URLS.UNSPLASH}photo-1515886657613-9f3515b0c78f?w=400` },
      { instruction: "Sitting professionally in a leather armchair in a luxury hotel lounge, discussing business.", labelKey: "pose_label_pro_lounge_sit", previewUrl: `${CDN_URLS.UNSPLASH}photo-1556761175-5973dc0f32e7?w=400` },
      { instruction: "Standing confidently in the lobby of a luxury hotel, ready for a meeting.", labelKey: "pose_label_pro_lounge_stand", previewUrl: `${CDN_URLS.UNSPLASH}photo-1560250097-0b93528c311a?w=400` },
      { instruction: "Standing formally in front of a giant Christmas tree inside City Hall.", labelKey: "pose_label_city_hall_tree", previewUrl: `${CDN_URLS.UNSPLASH}photo-1512389142860-9c449e58a543?w=400` },
      { instruction: "Walking briskly past the decorated columns of City Hall during the holidays.", labelKey: "pose_label_city_hall_walk", previewUrl: `${CDN_URLS.UNSPLASH}photo-1515886657613-9f3515b0c78f?w=400` },
      { instruction: "Arms crossed tightly, keeping warm", labelKey: "pose_label_cold_arms_crossed", previewUrl: `${CDN_URLS.UNSPLASH}photo-1515886657613-9f3515b0c78f?w=400` },
      { instruction: "Hands in pockets, bracing against cold wind", labelKey: "pose_label_cold_pockets", previewUrl: `${CDN_URLS.UNSPLASH}photo-1515886657613-9f3515b0c78f?w=400` },
      { instruction: "Blowing into hands to warm them up", labelKey: "pose_label_cold_warming_hands", previewUrl: `${CDN_URLS.UNSPLASH}photo-1515886657613-9f3515b0c78f?w=400` },
      { instruction: "Shivering slightly, shoulders hunched", labelKey: "pose_label_cold_shiver", previewUrl: `${CDN_URLS.UNSPLASH}photo-1515886657613-9f3515b0c78f?w=400` },
      { instruction: "Rubbing arms to stay warm", labelKey: "pose_label_cold_rubbing_arms", previewUrl: `${CDN_URLS.UNSPLASH}photo-1515886657613-9f3515b0c78f?w=400` },
    ]
  },
  {
    nameKey: "pose_category_glamour",
    requiredMembership: 'advanced',
    poses: [
      { instruction: "Leaning against a railing with a city skyline in the background at golden hour, looking thoughtful.", labelKey: "pose_label_cityscape", previewUrl: `${CDN_URLS.UNSPLASH}photo-1477959858617-67f85cf4f1df?w=400` },
      { instruction: "Glamorous red carpet pose, one hand on hip, looking over the shoulder at the camera with a slight smile.", labelKey: "pose_label_red_carpet", previewUrl: `${CDN_URLS.UNSPLASH}photo-1569388330292-79cc1ec67270?w=400` },
      { instruction: "Glamorous red carpet pose, front view, hands on hips.", labelKey: "pose_label_red_carpet_front", previewUrl: `${CDN_URLS.UNSPLASH}photo-1517841905240-472988babdf9?w=400` },
      { instruction: "Classic Hollywood glamour pose, standing with a gentle S-curve, hand brushing against hair, looking alluringly at the camera.", labelKey: "pose_label_hollywood", previewUrl: `${CDN_URLS.UNSPLASH}photo-1515886657613-9f3515b0c78f?w=400` },
      { instruction: "Dramatic red carpet entrance, walking down a grand staircase, looking back at the camera.", labelKey: "pose_label_grand_entrance", previewUrl: `${CDN_URLS.UNSPLASH}photo-1566737236500-c8ac43014a67?w=400` },
      { instruction: "Elegant formal pose for an evening gown, showcasing the silhouette with a graceful posture.", labelKey: "pose_label_formal", previewUrl: `${CDN_URLS.UNSPLASH}photo-1566174053879-31528523f8ae?w=400` },
      { instruction: "Dynamic, joyful dancing pose, captured mid-twirl with flowing movement.", labelKey: "pose_label_joyful_twirl", previewUrl: `${CDN_URLS.UNSPLASH}photo-1504609773096-104ff2c73ba4?w=400` },
      { instruction: "Stepping out of a luxury car, looking elegant.", labelKey: "pose_label_luxury_car", previewUrl: `${CDN_URLS.UNSPLASH}photo-1552519507-da3b142c6e3d?w=400` },
      { instruction: "Adjusting sunglasses, looking cool and detached.", labelKey: "pose_label_sunglasses_cool", previewUrl: `${CDN_URLS.UNSPLASH}photo-1506794778202-cad84cf45f1d?w=400` },
      { instruction: "Looking over a balcony with a glass of wine, elegant evening wear, soft city lights in background.", labelKey: "pose_label_balcony_evening", previewUrl: `${CDN_URLS.UNSPLASH}photo-1517841905240-472988babdf9?w=400` },
      { instruction: "Sitting on a velvet chaise lounge, relaxed but elegant, looking at the camera.", labelKey: "pose_label_chaise_lounge", previewUrl: `${CDN_URLS.UNSPLASH}photo-1534528741775-53994a69daeb?w=400` },
      { instruction: "Applying lipstick in a mirror, reflection visible, getting ready for a night out.", labelKey: "pose_label_mirror_makeup", previewUrl: `${CDN_URLS.UNSPLASH}photo-1487412720507-e7ab37603c6f?w=400` },
      { instruction: "Dramatic high-fashion pose with dramatic lighting, hands framing the face.", labelKey: "pose_label_high_fashion_hands", previewUrl: `${CDN_URLS.UNSPLASH}photo-1500917293891-ef795e70e1f6?w=400` },
      { instruction: "Sitting on a high stool, legs crossed elegantly, showcasing shoes.", labelKey: "pose_label_stool_elegance", previewUrl: `${CDN_URLS.OCI_STORE}${OCI_FOLDERS.CONTENT}Sport/sport_444.jpg` },
      { instruction: "Standing with a flowing cape or dress caught in the wind, dramatic and ethereal.", labelKey: "pose_label_windy_drama", previewUrl: `${CDN_URLS.UNSPLASH}photo-1502989642968-94fbdc9eace4?w=400` },
      { instruction: "Posing on a Vintage Hollywood Film Set, cinematic lighting, classic aesthetic.", labelKey: "pose_label_vintage_film_set", previewUrl: `${CDN_URLS.UNSPLASH}photo-1496369644508-5c386928c85d?w=400` },
      { instruction: "Standing on an Award Show Stage with Spotlight, holding a trophy, dramatic backlighting.", labelKey: "pose_label_award_stage", previewUrl: `${CDN_URLS.UNSPLASH}photo-1516450360452-9312f5e86fc7?w=400` },
      { instruction: "Walking down a Modern Art Museum Hallway, high ceilings, architectural lines.", labelKey: "pose_label_museum_hallway", previewUrl: `${CDN_URLS.UNSPLASH}photo-1518998053901-5348d3969105?w=400` },
      { instruction: "Browsing in a High-End Fashion Boutique, holding a designer bag, chic interior.", labelKey: "pose_label_boutique", previewUrl: `${CDN_URLS.UNSPLASH}photo-1441986300917-64674bd600d8?w=400` },
      { instruction: "Descending a Minimalist Architectural Staircase, floating steps, glass railing.", labelKey: "pose_label_arch_staircase", previewUrl: `${CDN_URLS.UNSPLASH}photo-1505576399279-565b52d4ac71?w=400` },
      { instruction: "Standing in a grand Marble Foyer, high gloss floors, luxury chandelier above.", labelKey: "pose_label_marble_foyer", previewUrl: `${CDN_URLS.UNSPLASH}photo-1600607686527-6fb886090705?w=400` },
      { instruction: "High Fashion Crouch, angular limbs", labelKey: "pose_label_fashion_crouch", previewUrl: `${CDN_URLS.UNSPLASH}photo-1515886657613-9f3515b0c78f?w=400` },
      { instruction: "Broken Doll Pose, avant-garde", labelKey: "pose_label_broken_doll", previewUrl: `${CDN_URLS.UNSPLASH}photo-1515886657613-9f3515b0c78f?w=400` },
      { instruction: "Leaning forward, intense gaze, hands on knees", labelKey: "pose_label_intense_gaze", previewUrl: `${CDN_URLS.UNSPLASH}photo-1515886657613-9f3515b0c78f?w=400` },
      { instruction: "Back arch, head thrown back, dramatic", labelKey: "pose_label_back_arch", previewUrl: `${CDN_URLS.UNSPLASH}photo-1515886657613-9f3515b0c78f?w=400` },
      { instruction: "Hand framing face, beauty shot", labelKey: "pose_label_face_frame", previewUrl: `${CDN_URLS.UNSPLASH}photo-1515886657613-9f3515b0c78f?w=400` },
      { instruction: "Waving gracefully to the crowd", labelKey: "pose_label_waving_crowd", previewUrl: `${CDN_URLS.UNSPLASH}photo-1515886657613-9f3515b0c78f?w=400` },
      { instruction: "Posing for paparazzi, chin up", labelKey: "pose_label_paparazzi", previewUrl: `${CDN_URLS.UNSPLASH}photo-1515886657613-9f3515b0c78f?w=400` },
    ]
  },
  {
    nameKey: "pose_category_lifestyle",
    requiredMembership: 'advanced',
    poses: [
      { instruction: "Candid city walk, crossing a stylish urban street, captured mid-stride with natural movement.", labelKey: "pose_label_city_stride", previewUrl: `${CDN_URLS.UNSPLASH}photo-1483985988355-763728e1935b?w=400` },
      { instruction: "Enjoying a coffee at an outdoor city cafe, looking relaxed and candid.", labelKey: "pose_label_cafe_moment", previewUrl: `${CDN_URLS.UNSPLASH}photo-1554118811-1e0d58224f24?w=400` },
      { instruction: "Casually seated on a stool in a studio setting, looking directly at the camera with a relaxed smile.", labelKey: "pose_label_seated_casual", previewUrl: `${CDN_URLS.UNSPLASH}photo-1534528741775-53994a69daeb?w=400` },
      { instruction: "Relaxed seated pose on a plush armchair, reading a book, exuding comfort and intellect.", labelKey: "pose_label_reading_nook", previewUrl: `${CDN_URLS.UNSPLASH}photo-1519689680058-324335c77eba?w=400` },
      { instruction: "Relaxed pose, sitting on the steps of a grand, sunlit staircase, looking off-camera.", labelKey: "pose_label_staircase_sit", previewUrl: `${CDN_URLS.UNSPLASH}photo-1516575334481-f85287c2c82d?w=400` },
      { instruction: "Energetic pose, captured mid-run along a scenic beach path at sunrise.", labelKey: "pose_label_beach_run", previewUrl: `${CDN_URLS.UNSPLASH}photo-1502224562085-639556652f33?w=400` },
      { instruction: "Artistic pose, standing as if a statue in a modern art gallery, with dramatic lighting.", labelKey: "pose_label_art_gallery", previewUrl: `${CDN_URLS.UNSPLASH}photo-1545959826-c62971349296?w=400` },
      { instruction: "Spooky and fun Halloween pose, standing in front of a carved pumpkin patch at dusk, with a hint of fog.", labelKey: "pose_label_halloween", previewUrl: `${CDN_URLS.UNSPLASH}photo-1508349937151-22b68b72d5b1?w=400` },
      { instruction: "Joyful Easter pose, sitting in a field of spring flowers, holding a small basket of colorful eggs.", labelKey: "pose_label_easter", previewUrl: `${CDN_URLS.UNSPLASH}photo-1522748906645-95d8adfd52c7?w=400` },
      { instruction: "Walking through a Busy Downtown Crosswalk, yellow cabs in background, urban energy.", labelKey: "pose_label_downtown_crosswalk", previewUrl: `${CDN_URLS.UNSPLASH}photo-1477959858617-67f85cf4f1df?w=400` },
      { instruction: "Relaxing at a Luxury Hotel Coffee Bar, marble counters, elegant seating.", labelKey: "pose_label_hotel_bar", previewUrl: `${CDN_URLS.UNSPLASH}photo-1570554886154-9c91f352618b?w=400` },
      { instruction: "Socializing at a Rooftop Lounge at Sunset, holding a cocktail, city skyline view.", labelKey: "pose_label_rooftop_lounge", previewUrl: `${CDN_URLS.UNSPLASH}photo-1518643571672-8589bdc22367?w=400` },
      { instruction: "Viewing art in a Minimalist Art Gallery, white walls, contemplating a piece.", labelKey: "pose_label_art_gallery_minimal", previewUrl: `${CDN_URLS.UNSPLASH}photo-1554907984-15263bfd63bd?w=400` },
      { instruction: "Sitting on a Sunny Park Bench, reading a paper, green trees and soft light.", labelKey: "pose_label_park_bench", previewUrl: `${CDN_URLS.UNSPLASH}photo-1562237889-4b63309a4732?w=400` },
      { instruction: "Waiting on a Subway Station Platform, tiled walls, urban transit vibe.", labelKey: "pose_label_subway", previewUrl: `${CDN_URLS.UNSPLASH}photo-1465828768086-18f4739542be?w=400` },
      { instruction: "Surrounded by plants in a Botanical Garden Greenhouse, soft diffused natural light.", labelKey: "pose_label_greenhouse", previewUrl: `${CDN_URLS.UNSPLASH}photo-1505570555227-c51c7c174694?w=400` },
      { instruction: "Standing in an Urban Loft with Brick Walls, large industrial windows, modern furniture.", labelKey: "pose_label_urban_loft", previewUrl: `${CDN_URLS.UNSPLASH}photo-1505691938895-1758d7feb511?w=400` },
      { instruction: "Crouching down, tying shoelace", labelKey: "pose_label_tying_shoe", previewUrl: `${CDN_URLS.UNSPLASH}photo-1515886657613-9f3515b0c78f?w=400` },
      { instruction: "Sitting cross-legged on floor", labelKey: "pose_label_sitting_cross_legged", previewUrl: `${CDN_URLS.UNSPLASH}photo-1515886657613-9f3515b0c78f?w=400` },
    ]
  },
  {
    nameKey: "pose_category_seasonal_advanced",
    requiredMembership: 'advanced',
    poses: [
      { instruction: "Joyful winter pose, throwing fresh snow in the air in a dense pine forest. Majestic snow-capped mountains are visible in the background under an overcast dawn sky with pastel hues. The scene is unmistakably a cold, snowy winter forest.", labelKey: "pose_label_winter_forest_fun", previewUrl: `${CDN_URLS.UNSPLASH}photo-1482867996988-29ec3a0f1aac?w=400` },
      { instruction: "Relaxed apres-ski pose, sitting inside a cozy wooden chalet by a large window, holding a warm drink. Outside, multiple cozy chalets have lit fireplaces, with warm light spilling from the windows onto the deep blue twilight snow. A true winter evening atmosphere.", labelKey: "pose_label_apreski_chalet", previewUrl: `${CDN_URLS.UNSPLASH}photo-1516483638261-f4dbaf036963?w=400` },
      { instruction: "Festive holiday pose, standing elegantly next to a beautifully decorated Christmas tree with warm, twinkling lights in a cozy, dimly lit living room with a fireplace.", labelKey: "pose_label_christmas", previewUrl: `${CDN_URLS.UNSPLASH}photo-1512389142860-9c449e58a543?w=400` },
      { instruction: "A magical winter wonderland pose, standing in a vast snowy mountain landscape with hands in pockets, looking directly at the camera. The scene is pristine white with sharp, snow-capped peaks in the distance.", labelKey: "pose_label_winter_wonderland", previewUrl: `${CDN_URLS.UNSPLASH}photo-1486322632997-7b73697c7907?w=400` },
      { instruction: "Leaning against a wooden railing on a rustic chalet terrace, looking off-camera at a breathtaking snowy mountain landscape. The background features layers of alpine peaks and a clear winter sky.", labelKey: "pose_label_chalet_terrace", previewUrl: `${CDN_URLS.UNSPLASH}photo-1518098268026-4e187743363b?w=400` },
      { instruction: "Sitting in a rustic ski chalet interior by a roaring stone fireplace. The room is filled with warm light, exposed wooden beams, and a cozy rug. Outside the window, snow is falling gently.", labelKey: "pose_label_chalet_fireplace", previewUrl: `${CDN_URLS.UNSPLASH}photo-1542718610-a1d656d1884c?w=400` },
      { instruction: "Relaxed seated pose in an elegant, modern ski chalet living room with floor-to-ceiling windows overlooking a snowy mountain panorama. The interior features polished wood, plush furniture, and a sophisticated winter atmosphere.", labelKey: "pose_label_chalet_elegant_living", previewUrl: `${CDN_URLS.UNSPLASH}photo-1512917774080-9991f1c4c750?w=400` },
      { instruction: "Lying relaxed on a wooden sun lounger on the terrace of a high-altitude mountain chalet, looking out at a beautiful, sunny mountain landscape covered in brilliant white snow.", labelKey: "pose_label_lying_lounge", previewUrl: `${CDN_URLS.UNSPLASH}photo-1551524559-8af4e66a3239?w=400` },
      { instruction: "Walking towards a warm, inviting ski lodge at night during a gentle snowfall. The lodge glows with warm golden light against the deep blue night, with majestic snow-capped mountains looming in the background.", labelKey: "pose_label_chalet_night_snow", previewUrl: `${CDN_URLS.UNSPLASH}photo-1485594050903-8e8ee5326c90?w=400` },
      { instruction: "Dynamic and fun pose on a sunny ski slope during an après-ski party, holding ski poles, with a festive atmosphere, snow everywhere, and other skiers in the background enjoying the sunny winter day.", labelKey: "pose_label_slope_party", previewUrl: `${CDN_URLS.UNSPLASH}photo-1551698618-1dfe5d97d256?w=400` },
      { instruction: "Reclining elegantly on a velvet sofa in a dimly lit luxury hotel lounge.", labelKey: "pose_label_adv_lounge_sofa", previewUrl: `${CDN_URLS.UNSPLASH}photo-1572025442646-866d16c84a54?w=400` },
      { instruction: "Leaning against the marble bar of a luxury hotel lounge, holding a crystal glass.", labelKey: "pose_label_adv_lounge_bar", previewUrl: `${CDN_URLS.UNSPLASH}photo-1514362545857-3bc16c4c7d1b?w=400` },
      { instruction: "Posing dramatically on the snow-dusted steps of City Hall, framed by festive wreaths.", labelKey: "pose_label_adv_city_hall_steps", previewUrl: `${CDN_URLS.UNSPLASH}photo-1543589077-47d81606c1bf?w=400` },
      { instruction: "Standing on a balcony overlooking the City Hall Christmas market lights at night.", labelKey: "pose_label_adv_city_hall_balcony", previewUrl: `${CDN_URLS.UNSPLASH}photo-1512418490979-92798cec1380?w=400` },
    ]
  },
  {
    nameKey: "pose_category_countryside_activities",
    requiredMembership: 'advanced',
    poses: [
      { instruction: "Sitting elegantly on a horse, posture upright, holding reins, English riding style.", labelKey: "pose_label_riding_mounted", previewUrl: `${CDN_URLS.UNSPLASH}photo-1553284965-83fd3e82fa5a?w=400` },
      { instruction: "Standing next to a horse, one hand resting on the saddle, wearing equestrian gear.", labelKey: "pose_label_riding_standing", previewUrl: `${CDN_URLS.UNSPLASH}photo-1605152276897-4f618f831968?w=400` },
      { instruction: "Standing in a countryside field, holding a shotgun safely broken open over the arm, classic hunting attire.", labelKey: "pose_label_hunting_ready", previewUrl: `${CDN_URLS.UNSPLASH}photo-1516934024742-b461fba47600?w=400` },
      { instruction: "Walking through tall grass in a field, wearing tweed, with a hunting dog beside.", labelKey: "pose_label_hunting_walking", previewUrl: `${CDN_URLS.UNSPLASH}photo-1599598425947-2b0c392b4962?w=400` },
      { instruction: "Leaning casually against a wooden fence in a pasture, looking relaxed.", labelKey: "pose_label_country_fence", previewUrl: `${CDN_URLS.UNSPLASH}photo-1472396961693-142e6e596e35?w=400` },
      { instruction: "Sitting on a picnic blanket in a meadow, holding a book or drink.", labelKey: "pose_label_country_picnic", previewUrl: `${CDN_URLS.UNSPLASH}photo-1562237889-4b63309a4732?w=400` },
    ]
  },
  {
    nameKey: "pose_category_seasonal_countryside",
    requiredMembership: 'advanced',
    poses: [
      { instruction: "Riding a horse leisurely through a field of blooming spring daffodils with soft morning sunlight.", labelKey: "pose_label_spring_equestrian", previewUrl: `${CDN_URLS.UNSPLASH}photo-1551884831-bbf3ddd780ef?w=400` },
      { instruction: "Standing confidently in polo gear on a manicured green lawn on a bright summer day, holding a mallet.", labelKey: "pose_label_summer_polo", previewUrl: `${CDN_URLS.UNSPLASH}photo-1560868746-225567b758c9?w=400` },
      { instruction: "Traditional shooting stance in a dense forest with golden autumn leaves, wearing tweed hunting attire.", labelKey: "pose_label_autumn_shooting", previewUrl: `${CDN_URLS.UNSPLASH}photo-1505846637177-2c120b764d77?w=400` },
      { instruction: "Mounted on a horse in a snowy landscape during winter, wearing a heavy elegant riding coat.", labelKey: "pose_label_winter_riding", previewUrl: `${CDN_URLS.UNSPLASH}photo-1516483638261-f4dbaf036963?w=400` },
    ]
  },
  {
    nameKey: "pose_category_motorsport",
    requiredMembership: 'advanced',
    poses: [
      { instruction: "Standing confidently next to a Formula 1 car in the pit lane, holding a racing helmet.", labelKey: "pose_label_f1_standing", previewUrl: `${CDN_URLS.UNSPLASH}photo-1532906619279-a79716766d03?w=400` },
      { instruction: "Leaning casually against a Formula E car on a futuristic city street circuit.", labelKey: "pose_label_formula_e", previewUrl: `${CDN_URLS.UNSPLASH}photo-1568605117036-5fe5e7bab0b7?w=400` },
      { instruction: "Standing with arms crossed next to a NASCAR stock car on a race track bank.", labelKey: "pose_label_nascar", previewUrl: `${CDN_URLS.UNSPLASH}photo-1580273916550-e323be2eb5dd?w=400` },
      { instruction: "Walking away from a GT racing car in the paddock, looking back over shoulder.", labelKey: "pose_label_gtm", previewUrl: `${CDN_URLS.UNSPLASH}photo-1552176625-e47ff529b595?w=400` },
      { instruction: "Sitting on the front tire of a rally car covered in mud, celebrating a win.", labelKey: "pose_label_rally", previewUrl: `${CDN_URLS.UNSPLASH}photo-1568605114967-8130f3a36994?w=400` },
    ]
  },
  {
    nameKey: "pose_category_arena_sports",
    requiredMembership: 'premium',
    poses: [
      { instruction: "Dynamic basketball pose, dribbling the ball low on a hardwood court with stadium lights.", labelKey: "pose_label_basketball_dribble", previewUrl: `${CDN_URLS.UNSPLASH}photo-1519861531473-920026393112?w=400` },
      { instruction: "Mid-air basketball slam dunk pose, reaching for the hoop.", labelKey: "pose_label_basketball_dunk", previewUrl: `${CDN_URLS.UNSPLASH}photo-1546519638-68e109498ffc?w=400` },
      { instruction: "Ice hockey player winding up for a powerful slapshot on the ice.", labelKey: "pose_label_hockey_slapshot", previewUrl: `${CDN_URLS.UNSPLASH}photo-1515703407324-5f75366f58b7?w=400` },
      { instruction: "Ice hockey player celebrating a goal, arms raised, on the rink.", labelKey: "pose_label_hockey_celebration", previewUrl: `${CDN_URLS.UNSPLASH}photo-1580748141549-71748dbe0bdc?w=400` },
    ]
  }
];

// Flatten the categories for components that need a simple list.
const allPoses = POSE_CATEGORIES.flatMap(category => category.poses);
export const POSE_INSTRUCTIONS = allPoses.map(p => p.instruction);
export const POSE_LABEL_KEYS = allPoses.map(p => p.labelKey);
export const POSE_PREVIEW_URLS = allPoses.map(p => p.previewUrl);

export const VIEW_360_POSES = [
  POSE_CATEGORIES[0].poses[1].instruction,
  POSE_CATEGORIES[0].poses[2].instruction,
  POSE_CATEGORIES[0].poses[3].instruction,
  "Side profile view, standing straight, facing left.",
];

